import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import CategoryTabs from "@/components/feature/CategoryTabs";
import ArticleCard from "@/components/feature/ArticleCard";
import Pagination from "@/components/feature/Pagination";
import EmptyState from "@/components/feature/EmptyState";
import { getReviewsByCategory, getCategoryMeta } from "@/lib/content";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

const PAGE_SIZE = 6;

export default function Reviews() {
  const { category } = useParams();
  const all = getReviewsByCategory();
  const meta = category ? getCategoryMeta(category) : undefined;
  const titleText = category ? `${category}评测` : "车型评测";

  const brands = useMemo(
    () => Array.from(new Set(all.map((item) => item.brand))),
    [all],
  );
  const levels = useMemo(
    () => Array.from(new Set(all.map((item) => item.level))),
    [all],
  );

  const [brand, setBrand] = useState("all");
  const [level, setLevel] = useState("all");
  const [sort, setSort] = useState<"latest" | "hot">("latest");
  const [page, setPage] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);

  useDocumentMeta({
    title: `汽车资讯网 - ${titleText}`,
    description: meta
      ? `${meta.desc} 提供${category}车型的深度试驾与专业评测，帮助您对比选购。`
      : "提供轿车、SUV、新能源车、性能跑车等车型的深度试驾体验与专业评测内容。",
    keywords: `车型评测,${category ? category + "," : ""}汽车评测,试驾,购车参考`,
  });

  useEffect(() => {
    setPage(1);
  }, [category, brand, level, sort]);

  const filtered = useMemo(() => {
    let list = getReviewsByCategory(category);
    if (brand !== "all") list = list.filter((item) => item.brand === brand);
    if (level !== "all") list = list.filter((item) => item.level === level);
    list = [...list].sort((a, b) =>
      sort === "hot" ? b.views - a.views : a.date < b.date ? 1 : -1,
    );
    return list;
  }, [category, brand, level, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const items = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const reset = () => {
    setBrand("all");
    setLevel("all");
    setSort("latest");
    setPage(1);
  };

  return (
    <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-6 md:py-8">
      <Breadcrumb items={[{ label: "评测", to: "/reviews" }, ...(category ? [{ label: category }] : [])]} />

      <div className="mt-4 bg-background-50 border border-background-200 rounded-lg p-6 md:p-8">
        <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950">
          {titleText}
        </h1>
        <p className="mt-2 text-sm md:text-base text-foreground-600 max-w-3xl">
          {meta ? meta.intro : "汇聚专业评测团队的深度试驾报告与对比分析，从动力、能耗、空间到智能体验，为你提供可靠的购车参考。"}
        </p>
      </div>

      <div className="mt-6">
        <CategoryTabs basePath="/reviews" active={category} allLabel="全部评测" />
      </div>

      <div className="mt-6 bg-background-50 border border-background-200 rounded-lg">
        <div className="flex items-center justify-between px-4 md:px-5 py-3 border-b border-background-200">
          <span className="text-sm text-foreground-600">
            共找到 <strong className="text-primary-700">{filtered.length}</strong> 篇评测
          </span>
          <button
            type="button"
            onClick={() => setFilterOpen((v) => !v)}
            className="md:hidden h-9 px-3 rounded-md border border-background-300 text-sm text-foreground-700 flex items-center gap-1 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-filter-3-line" />
            筛选
          </button>
        </div>

        <div
          className={`${filterOpen ? "flex" : "hidden"} md:flex flex-col md:flex-row md:items-center gap-4 md:gap-8 px-4 md:px-5 py-4 border-b border-background-200`}
        >
          <label className="flex items-center gap-2">
            <span className="text-xs text-foreground-500 whitespace-nowrap">品牌</span>
            <select
              value={brand}
              onChange={(e) => setBrand(e.target.value)}
              className="h-9 px-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-800 outline-none cursor-pointer"
            >
              <option value="all">全部品牌</option>
              {brands.map((b) => (
                <option key={b} value={b}>
                  {b}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-2">
            <span className="text-xs text-foreground-500 whitespace-nowrap">级别</span>
            <select
              value={level}
              onChange={(e) => setLevel(e.target.value)}
              className="h-9 px-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-800 outline-none cursor-pointer"
            >
              <option value="all">全部级别</option>
              {levels.map((l) => (
                <option key={l} value={l}>
                  {l}
                </option>
              ))}
            </select>
          </label>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-foreground-500 mr-1">排序</span>
            <button
              type="button"
              onClick={() => setSort("latest")}
              className={`px-3 py-1.5 rounded-full text-xs cursor-pointer whitespace-nowrap transition-colors ${
                sort === "latest"
                  ? "bg-primary-600 text-background-50"
                  : "bg-background-100 text-foreground-700 hover:bg-background-200"
              }`}
            >
              最新
            </button>
            <button
              type="button"
              onClick={() => setSort("hot")}
              className={`px-3 py-1.5 rounded-full text-xs cursor-pointer whitespace-nowrap transition-colors ${
                sort === "hot"
                  ? "bg-accent-500 text-primary-950"
                  : "bg-background-100 text-foreground-700 hover:bg-background-200"
              }`}
            >
              最热
            </button>
          </div>
        </div>

        <div className="p-4 md:p-5">
          {items.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {items.map((item) => (
                <ArticleCard key={item.id} article={item} variant="grid" />
              ))}
            </div>
          ) : (
            <EmptyState
              title="没有找到相关评测"
              desc="当前品牌或车型级别下暂无评测内容，点击下方按钮重置筛选条件。"
              actionLabel="重置筛选"
              onAction={reset}
            />
          )}

          <Pagination page={safePage} totalPages={totalPages} onChange={setPage} />
        </div>
      </div>
    </div>
  );
}